import { addDays } from 'sling-calendar-sdk/utils';

import config from './config';
import notFoundUrl from './404.html';
import modalConsentUrl from './account/modal-consent.html';
import ConsentCtrl from './account/ConsentCtrl';

export default function appConfig(
  $stateProvider,
  $urlRouterProvider,
  $locationProvider,
  cfpLoadingBarProvider,
  AnnouncementServiceProvider,
  RestangularProvider,
  $httpProvider,
  $uibModalProvider,
  uibDatepickerConfig,
  uibDatepickerPopupConfig,
  $sceDelegateProvider,
  $cookiesProvider,
  $compileProvider,
  $qProvider,
  $provide,
  $animateProvider,
  IgnoreServerErrorsProvider,
  vsRepeatConfigProvider
) {
  'ngInject';

  // Redirect if none of the routes matched
  $urlRouterProvider.when('', '/');
  $urlRouterProvider.otherwise('/404');

  if (config.SILENCE_VS_REPEAT_WARNINGS) {
    vsRepeatConfigProvider.silenceSizeMismatchWarnings();
  }

  $stateProvider.state('404', {
    url: '/404',
    templateUrl: notFoundUrl,
    data: {
      title: 'Page not found',
    },
  });

  $stateProvider.state('accountActivity', {
    url: '/accountActivity',
    template:
      '<div class="container margin-top-40"><r-account-activity user="$resolve.user"></r-account-activity></div>',
    data: {
      title: 'Account activity',
    },
    resolve: {
      user(Authentication) {
        'ngInject';

        return Authentication.user;
      },
    },
  });

  $stateProvider.state('old-user-verify', {
    url: '/user/verify/:code',
    data: {
      redirectToLoginApp: true,
    },
    onEnter(WindowLocation, $transition$) {
      WindowLocation.href = `${config.SLING_LOGIN_URL}/user/verify/${$transition$.params().code}`;
    },
  });

  $stateProvider.state('old-team-verify', {
    url: '/team/verify/:code',
    data: {
      redirectToLoginApp: true,
    },
    onEnter(WindowLocation, $transition$) {
      WindowLocation.href = `${config.SLING_LOGIN_URL}/team/verify/${$transition$.params().code}`;
    },
  });

  RestangularProvider.setBaseUrl(config.API_END_POINT);
  RestangularProvider.setRestangularFields({ selfLink: 'self.link', route: '_route' });
  RestangularProvider.addFullRequestInterceptor(
    (data, method, element, url, params, headers, httpConfig) => {
      RestangularProvider.setFullResponse(Boolean(httpConfig && httpConfig.fullResponse));
    }
  );
  $httpProvider.defaults.headers.delete = { 'Content-Type': 'application/json;charset=utf-8' };

  $httpProvider.defaults.headers.get = {
    'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: 0,
  };

  // Enabled html5 mode, doesn't use the # part in the  urls
  $locationProvider.html5Mode({ enabled: true, requireBase: false });

  $cookiesProvider.defaults.domain = config.COOKIE_DOMAIN;
  $cookiesProvider.defaults.expires = addDays(new Date(), config.COOKIE_EXPIRES_DAYS);
  $cookiesProvider.defaults.path = '/';

  // The top progress bar should not include the spinner, it's hidden by the menu anyway
  cfpLoadingBarProvider.includeSpinner = false;

  // Configure UI-Bootstrap's datepicker (startingDay: 0=sunday, 1=monday)
  uibDatepickerConfig.showWeeks = false;
  uibDatepickerConfig.formatDay = 'd';
  uibDatepickerConfig.startingDay = 1;
  uibDatepickerConfig.maxMode = 'month';
  uibDatepickerPopupConfig.showButtonBar = false;

  // Disable debug mode
  $compileProvider.debugInfoEnabled(config.DEBUG_INFO_ENABLED);

  $uibModalProvider.options.keyboard = true;

  if (__TEST__) {
    $uibModalProvider.options.animation = false;
    $uibModalProvider.options.backdrop = false;
  } else {
    $uibModalProvider.options.backdropClass = 'sling-animate';
  }

  const whitelist = $sceDelegateProvider.resourceUrlWhitelist();
  whitelist.push('https://w.soundcloud.com/**');
  whitelist.push('https://open.spotify.com/embed/track/**');
  $sceDelegateProvider.resourceUrlWhitelist(whitelist);

  IgnoreServerErrorsProvider.setRule(502, 'GET', [/^status/, /^sync/, /^users\/concise/]);

  IgnoreServerErrorsProvider.setRule(502, 'PUT', [/^notifications\/read/, /^ack\/.+/]);

  $animateProvider.classNameFilter(/sling-animate/);

  $provide.decorator('nvFileOverDirective', ($delegate) => {
    'ngInject';

    const directive = $delegate[0];
    const originalLink = directive.link;

    directive.compile = function compile() {
      return function link(...args) {
        const [, element, attrs] = args;
        const overClass = attrs.overClass || 'nv-file-over';
        originalLink.apply(this, args);
        element.on('dragleave', () => {
          element.removeClass(overClass);
        });
      };
    };

    return $delegate;
  });

  AnnouncementServiceProvider.add({
    type: 'consent',
    html: modalConsentUrl,
    controller: ConsentCtrl,
    modalSize: '700',
    isPermanent: true,
    async isIrrelevant(Authentication, $location, $rootScope) {
      'ngInject';

      if (Authentication.user.org.isToastBilling) {
        return true;
      }

      if (Authentication.user.consented || !$rootScope.finishedOnboarding) {
        return true;
      }

      const { code, autosetup } = $location.search();

      if (code || autosetup) {
        await Authentication.consent();
        return true;
      }

      return false;
    },
  });
}
